import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AlertService } from 'app/services/alert.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { SquareLocationProductionService } from '../services/square-location-production.service';
import * as SquareLocationProductionActions from './square-location-production.actions';

@Injectable()
export class SquareLocationProductionEffects {
    private _actions$ = inject(Actions);
    private _squareLocationProductionService = inject(
        SquareLocationProductionService
    );
    private _alertService = inject(AlertService);

    messages = {
        getAllError: 'Error getting all square location productions',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All
    getAllSquareLocationProductions$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                SquareLocationProductionActions.loadSquareLocationProductions
            ),
            switchMap(() =>
                this._squareLocationProductionService.getAll().pipe(
                    map((res) =>
                        SquareLocationProductionActions.loadSquareLocationProductionsSuccess(
                            {
                                squareLocationProductions:
                                    res.data.squareLocationProductions,
                            }
                        )
                    ),
                    catchError((error) =>
                        of(
                            SquareLocationProductionActions.loadSquareLocationProductionsFailure(
                                {
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                }
                            )
                        )
                    )
                )
            )
        )
    );

    // Get One
    getSquareLocationProduction$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                SquareLocationProductionActions.loadSquareLocationProduction
            ),
            switchMap((action) =>
                this._squareLocationProductionService.getOne(action.id).pipe(
                    map((res) =>
                        SquareLocationProductionActions.loadSquareLocationProductionSuccess(
                            { squareLocationProduction: res.data }
                        )
                    ),
                    catchError((error) =>
                        of(
                            SquareLocationProductionActions.loadSquareLocationProductionFailure(
                                {
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                }
                            )
                        )
                    )
                )
            )
        )
    );

    // Create
    createSquareLocationProduction$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                SquareLocationProductionActions.createSquareLocationProduction
            ),
            switchMap((action) =>
                this._squareLocationProductionService.create(action.data).pipe(
                    map((res) =>
                        SquareLocationProductionActions.createSquareLocationProductionSuccess(
                            {
                                squareLocationProduction: res.data,
                                success:
                                    'Square location production created successfully',
                            }
                        )
                    ),
                    catchError((error) =>
                        of(
                            SquareLocationProductionActions.createSquareLocationProductionFailure(
                                {
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                }
                            )
                        )
                    )
                )
            )
        )
    );

    // Update
    updateSquareLocationProduction$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                SquareLocationProductionActions.updateSquareLocationProduction
            ),
            switchMap((action) =>
                this._squareLocationProductionService
                    .update(action.data, action.id)
                    .pipe(
                        map((res) =>
                            SquareLocationProductionActions.updateSquareLocationProductionSuccess(
                                {
                                    squareLocationProduction: res.data,
                                    success:
                                        'Square location production updated successfully',
                                }
                            )
                        ),
                        catchError((error) =>
                            of(
                                SquareLocationProductionActions.updateSquareLocationProductionFailure(
                                    {
                                        error:
                                            error?.error?.message ||
                                            this.messages.unknownError,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    // Delete
    deleteSquareLocationProduction$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                SquareLocationProductionActions.deleteSquareLocationProduction
            ),
            switchMap((action) =>
                this._squareLocationProductionService.delete(action.id).pipe(
                    map(() =>
                        SquareLocationProductionActions.deleteSquareLocationProductionSuccess(
                            {
                                id: action.id,
                                success:
                                    'Square location production deleted successfully',
                            }
                        )
                    ),
                    catchError((error) =>
                        of(
                            SquareLocationProductionActions.deleteSquareLocationProductionFailure(
                                {
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                }
                            )
                        )
                    )
                )
            )
        )
    );

    // -----------------------------------------------------------
    // @ Side Effects (alerts)
    // -----------------------------------------------------------

    onSquareLocationProductionActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    SquareLocationProductionActions.createSquareLocationProductionSuccess,
                    SquareLocationProductionActions.updateSquareLocationProductionSuccess,
                    SquareLocationProductionActions.deleteSquareLocationProductionSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');
                })
            ),
        { dispatch: false }
    );

    onSquareLocationProductionActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    SquareLocationProductionActions.loadSquareLocationProductionsFailure,
                    SquareLocationProductionActions.loadSquareLocationProductionFailure,
                    SquareLocationProductionActions.createSquareLocationProductionFailure,
                    SquareLocationProductionActions.updateSquareLocationProductionFailure,
                    SquareLocationProductionActions.deleteSquareLocationProductionFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
